import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Phone, Mail, Calendar, Heart, Leaf, Globe, MapPin, Upload, FileText, Image, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FactoryHypeContactModal({ isOpen, onClose, onSubmit, sessionId }) {
  const [formData, setFormData] = useState({
    vorname: "",
    nachname: "",
    age: "",
    wohnort: "",
    phone: "",
    email: "",
    start_date: "Sofort",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Resume upload state - supports multiple files (up to 4)
  const [resumeFiles, setResumeFiles] = useState([]);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const MAX_FILES = 4;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vorname.trim()) {
      newErrors.vorname = "Bitte gib deinen Vornamen ein";
    }
    
    if (!formData.nachname.trim()) {
      newErrors.nachname = "Bitte gib deinen Nachnamen ein";
    }
    
    if (!formData.age || parseInt(formData.age) < 16) {
      newErrors.age = "Du musst mindestens 16 Jahre alt sein";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Bitte gib deine Telefonnummer ein";
    }
    
    if (!formData.email.trim() || !formData.email.includes("@")) {
      newErrors.email = "Bitte gib eine gültige E-Mail ein";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file selection - multiple files
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check total file count
    const totalFiles = resumeFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      setErrors(prev => ({ ...prev, resume: `Maximal ${MAX_FILES} Dateien erlaubt` }));
      return;
    }

    // Validate each file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, resume: "Nur PDF, JPG, PNG oder DOC/DOCX erlaubt" }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: `${file.name} ist zu groß (max 10MB)` }));
        return;
      }
    }

    setResumeFiles(prev => [...prev, ...files]);
    setErrors(prev => ({ ...prev, resume: null }));
  };

  // Remove a file from the list
  const removeFile = (index) => {
    setResumeFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload all resume files
  const uploadAllResumes = async () => {
    if (resumeFiles.length === 0) return [];
    
    setResumeUploading(true);
    const uploaded = [];
    
    try {
      for (const file of resumeFiles) {
        // Skip if already uploaded
        const alreadyUploaded = uploadedFiles.find(f => f.filename === file.name);
        if (alreadyUploaded) {
          uploaded.push(alreadyUploaded);
          continue;
        }
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('session_id', sessionId || '');
        formDataUpload.append('project_id', 'factoryhype');
        
        const response = await axios.post(`${API}/upload-resume`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          uploaded.push(response.data.file_info);
        }
      }
      
      setUploadedFiles(uploaded);
      return uploaded;
    } catch (error) {
      console.error("Resume upload error:", error);
      setErrors(prev => ({ ...prev, resume: "Upload fehlgeschlagen. Bitte versuche es erneut." }));
      return uploaded;
    } finally {
      setResumeUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      // Upload all resume files first
      let uploadedResumeFiles = uploadedFiles;
      if (resumeFiles.length > 0 && uploadedFiles.length < resumeFiles.length) {
        uploadedResumeFiles = await uploadAllResumes();
      }
      
      await onSubmit({
        ...formData,
        first_name: `${formData.vorname} ${formData.nachname}`,
        age: parseInt(formData.age),
        session_id: sessionId,
        resume_files: uploadedResumeFiles.length > 0 ? uploadedResumeFiles : null
      });
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Heart className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Werde Teil der Bewegung!</h2>
                  <p className="text-sm text-emerald-100">FactoryHype - Dein Job mit Sinn</p>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[60vh]">
            {/* Vorname */}
            <div>
              <Label htmlFor="vorname" className="flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4 text-emerald-500" />
                Vorname
              </Label>
              <Input
                id="vorname"
                value={formData.vorname}
                onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                placeholder="Dein Vorname"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-vorname"
              />
              {errors.vorname && (
                <p className="text-red-500 text-xs mt-1">{errors.vorname}</p>
              )}
            </div>
            
            {/* Nachname */}
            <div>
              <Label htmlFor="nachname" className="flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4 text-emerald-500" />
                Nachname
              </Label>
              <Input
                id="nachname"
                value={formData.nachname}
                onChange={(e) => setFormData({ ...formData, nachname: e.target.value })}
                placeholder="Dein Nachname"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-nachname"
              />
              {errors.nachname && (
                <p className="text-red-500 text-xs mt-1">{errors.nachname}</p>
              )}
            </div>
            
            {/* Age */}
            <div>
              <Label htmlFor="age" className="flex items-center gap-2 text-slate-700">
                <Calendar className="w-4 h-4 text-emerald-500" />
                Alter
              </Label>
              <Input
                id="age"
                type="number"
                min="16"
                max="99"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                placeholder="Dein Alter (ab 18)"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-age"
              />
              {errors.age && (
                <p className="text-red-500 text-xs mt-1">{errors.age}</p>
              )}
            </div>
            
            {/* Wohnort */}
            <div>
              <Label htmlFor="wohnort" className="flex items-center gap-2 text-slate-700">
                <MapPin className="w-4 h-4 text-emerald-500" />
                Wohnort
              </Label>
              <Input
                id="wohnort"
                value={formData.wohnort}
                onChange={(e) => setFormData({ ...formData, wohnort: e.target.value })}
                placeholder="Dein Wohnort"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-wohnort"
              />
            </div>
            
            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="flex items-center gap-2 text-slate-700">
                <Phone className="w-4 h-4 text-emerald-500" />
                Telefon
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Deine Handynummer"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-phone"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <Label htmlFor="email" className="flex items-center gap-2 text-slate-700">
                <Mail className="w-4 h-4 text-emerald-500" />
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Deine E-Mail-Adresse"
                className="mt-1 border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-email"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            
            {/* Start Date */}
            <div>
              <Label htmlFor="start_date" className="flex items-center gap-2 text-slate-700">
                <Leaf className="w-4 h-4 text-emerald-500" />
                Wann könntest du starten?
              </Label>
              <select
                id="start_date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="mt-1 w-full rounded-md border border-emerald-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="t2m-form-start"
              >
                <option value="Sofort">Sofort</option>
                <option value="In 1 Woche">In 1 Woche</option>
                <option value="In 2 Wochen">In 2 Wochen</option>
                <option value="In 1 Monat">In 1 Monat</option>
                <option value="Später">Später</option>
              </select>
            </div>
            
            {/* Message */}
            <div>
              <Label htmlFor="message" className="flex items-center gap-2 text-slate-700">
                <Globe className="w-4 h-4 text-emerald-500" />
                Nachricht (optional)
              </Label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Erzähl uns von dir..."
                rows={3}
                className="mt-1 w-full rounded-md border border-emerald-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-emerald-400 resize-none"
                data-testid="t2m-form-message"
              />
            </div>
            
            {/* Resume Upload - Multiple Files */}
            <div className="pt-2">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-amber-800">
                  💡 <strong>Tipp:</strong> Wenn du direkt einen Lebenslauf mit Foto hochlädst, können wir deine Bewerbung meist schneller und bevorzugt bearbeiten – damit hast du besonders gute Chancen auf den Job! 😊
                </p>
              </div>
              
              <Label htmlFor="resume" className="flex items-center gap-2 text-slate-700">
                <FileText className="w-4 h-4 text-emerald-500" />
                Lebenslauf + Foto hochladen (optional, max {MAX_FILES} Dateien)
              </Label>
              
              <div className="mt-2 space-y-2">
                {/* Show selected files */}
                {resumeFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      {file.type?.startsWith('image/') ? (
                        <Image className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-emerald-600" />
                      )}
                      <span className="text-sm text-slate-700 truncate max-w-[180px]">
                        {file.name}
                      </span>
                      {resumeUploading && (
                        <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                      )}
                      {uploadedFiles.find(f => f.filename === file.name) && (
                        <span className="text-xs text-emerald-600">✓</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {/* Upload button - show only if less than MAX_FILES */}
                {resumeFiles.length < MAX_FILES && (
                  <label 
                    htmlFor="resume-upload"
                    className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-emerald-500 mb-1" />
                    <span className="text-sm text-emerald-600">
                      {resumeFiles.length === 0 ? 'Dateien auswählen' : 'Weitere Datei hinzufügen'}
                    </span>
                    <span className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, DOC (max 10MB pro Datei)</span>
                    <input
                      id="resume-upload"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                      data-testid="t2m-form-resume"
                    />
                  </label>
                )}
              </div>
              
              {errors.resume && (
                <p className="text-red-500 text-xs mt-1">{errors.resume}</p>
              )}
            </div>
          </form>
          
          {/* Footer */}
          <div className="p-5 border-t border-slate-200 bg-slate-50">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-emerald-500/30"
              data-testid="t2m-form-submit"
            >
              {isSubmitting ? (
                "Wird gesendet..."
              ) : (
                <>
                  <Heart className="w-5 h-5 mr-2" />
                  Bewerbung absenden
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-3">
              Wir melden uns schnellstmöglich bei dir! 💚
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
